import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useApiClient } from "@src/packages/api/louise-api";
import { throwApiError } from "@src/packages/api/api-error";
import type { paths } from "@src/packages/openapi/schema";
import type { ResponseBody } from "@src/packages/openapi/helpers";
import type { SharingCategory } from "./usePatientSharing";

const PARTNER_RELATED_QUERY_ROOTS = new Set([
  "partner-invitation",
  "patient-sharing",
  "care-persons",
  "care-connections",
  "patient",
  "cycles",
  "active-cycle",
]);

function invalidatePartnerRelatedQueries(
  queryClient: ReturnType<typeof useQueryClient>,
): void {
  queryClient.invalidateQueries({
    predicate: (query) =>
      typeof query.queryKey[0] === "string" &&
      PARTNER_RELATED_QUERY_ROOTS.has(query.queryKey[0]),
  });
}

type PartnerInvitation = NonNullable<
  ResponseBody<
    paths["/{tenantId}/patients/{patientId}/partner/invitation"]["get"]
  >["data"]
>;

type SendPartnerInviteBody = {
  phone: string;
  firstName?: string;
  sharingCategories: SharingCategory[];
  sharingScope: PartnerSharingScope;
  /** Replace the currently pending invitation before creating the new one. */
  replaceExisting?: boolean;
};

// TODO: replace with the generated API type once the pinned dashboard spec
// includes the scoped partner-invite request fields.
export type PartnerSharingScope = {
  caseId: string;
  cycleId: string;
};

export function usePartnerInvitation() {
  const { client, tenantId, patientId } = useApiClient();
  const queryClient = useQueryClient();

  const query = useQuery<PartnerInvitation | null>({
    queryKey: ["partner-invitation", tenantId, patientId],
    queryFn: async () => {
      const { data, error, response } = await client.GET(
        "/patients/{patientId}/partner/invitation",
        {
          params: { path: { patientId } },
        },
      );
      if (data) return data.data;
      throwApiError(
        error,
        "Failed to load partner invitation",
        "usePartnerInvitation",
        response?.status,
      );
    },
    enabled: !!tenantId && !!patientId,
    // The partner accepts on their own device, so the inviter never gets a
    // signal. Poll while the invite is pending so the UI reflects acceptance
    // without a manual refresh; stop polling once it is no longer pending.
    refetchInterval: (q) =>
      q.state.data?.status === "pending" ? 20_000 : false,
  });

  // While the invitation is pending, also poll the patient query so the
  // inviter picks up partnerConnected as soon as the partner accepts.
  // Cleanup invalidates once to cover the race where the invitation poll
  // returns null before the patient poll has picked up the change.
  const isPending = query.data?.status === "pending";
  useEffect(() => {
    if (!isPending) return;
    const interval = setInterval(() => {
      queryClient.refetchQueries({
        queryKey: ["patient", tenantId, patientId],
      });
    }, 20_000);
    return () => {
      clearInterval(interval);
      queryClient.invalidateQueries({
        queryKey: ["patient", tenantId, patientId],
      });
    };
  }, [isPending, queryClient, tenantId, patientId]);

  return query;
}

export function useSendPartnerInvite() {
  const { client, patientId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: SendPartnerInviteBody) => {
      if (body.replaceExisting) {
        const { data, error, response } = await client.DELETE(
          "/patients/{patientId}/partner/invitation",
          {
            params: { path: { patientId } },
          },
        );
        if (error) {
          throwApiError(
            error,
            "Failed to replace partner invitation",
            "useSendPartnerInvite",
            response?.status,
          );
        }
        if (!data?.data?.success) {
          throw new Error(
            "The pending partner invitation could not be replaced.",
          );
        }
      }

      // The dashboard API exposes the scoped sharing fields before the
      // checked-in OpenAPI bundle is regenerated. Keep the extra fields at
      // this runtime boundary so the selected cycle permissions are sent to
      // the API.
      const requestBody = {
        phone: body.phone,
        firstName: body.firstName,
        sharingCategories: body.sharingCategories,
        sharingScope: body.sharingScope,
      };
      const { data, error, response } = await client.POST(
        "/patients/{patientId}/partner/invite",
        {
          params: { path: { patientId } },
          body: requestBody,
        },
      );
      if (error) {
        throwApiError(
          error,
          "Failed to send partner invitation",
          "useSendPartnerInvite",
          response?.status,
        );
      }
      // A successful HTTP status is not enough to show the sent state. The
      // invitation must be present and structurally usable so a malformed
      // 2xx response cannot leave the sender believing an invite exists.
      const invitation = data?.data;
      if (!invitation?.id || invitation.status !== "pending") {
        throw new Error("Partner invitation was not confirmed.");
      }
      return invitation;
    },
    onSuccess: () => {
      invalidatePartnerRelatedQueries(queryClient);
    },
  });
}

export function useCancelPartnerInvitation() {
  const { client, patientId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data, error, response } = await client.DELETE(
        "/patients/{patientId}/partner/invitation",
        {
          params: { path: { patientId } },
        },
      );
      if (error) {
        throwApiError(
          error,
          "Failed to cancel partner invitation",
          "useCancelPartnerInvitation",
          response?.status,
        );
      }
      if (!data?.data?.success) {
        throw new Error("Invitation cancellation was not confirmed.");
      }
      return data.data;
    },
    onSuccess: () => {
      invalidatePartnerRelatedQueries(queryClient);
    },
  });
}

export function useAcceptPartnerInvite() {
  const { client, patientId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invitationCode: string) => {
      const normalizedCode = invitationCode.trim();
      if (!normalizedCode || /\s/.test(normalizedCode)) {
        throw new Error("Partner invitation code is invalid.");
      }

      const { data, error, response } = await client.POST(
        "/patients/{patientId}/partner/accept",
        {
          params: { path: { patientId } },
          body: { invitationCode: normalizedCode },
        },
      );
      if (error) {
        throwApiError(
          error,
          "Failed to accept partner invitation",
          "useAcceptPartnerInvite",
          response?.status,
        );
      }
      const acceptance = data?.data;
      if (
        !acceptance?.success ||
        typeof acceptance.partnerPatientId !== "string" ||
        !acceptance.partnerPatientId.trim()
      ) {
        throw new Error("Partner acceptance was not confirmed.");
      }
      return acceptance;
    },
    onSuccess: () => {
      invalidatePartnerRelatedQueries(queryClient);
    },
  });
}

export function useUnlinkPartner() {
  const { client, patientId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data, error, response } = await client.DELETE(
        "/patients/{patientId}/partner",
        {
          params: { path: { patientId } },
        },
      );
      if (error) {
        throwApiError(
          error,
          "Failed to unlink partner",
          "useUnlinkPartner",
          response?.status,
        );
      }
      if (!data?.data?.success) {
        throw new Error("Partner separation was not confirmed.");
      }
      return data.data;
    },
    onSuccess: () => {
      invalidatePartnerRelatedQueries(queryClient);
    },
  });
}
